import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, ProtectedRoute } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AdminAuthProvider, AdminProtectedRoute } from './context/AdminAuthContext';
import { AdminThemeProvider } from './context/AdminThemeContext';
import { AdminNotificationsProvider } from './context/AdminNotificationsContext';
import { RealtimeProvider } from './context/RealtimeContext';
import { NotificationsProvider } from './context/NotificationsContext';
import CartModal from './components/CartModal';
import CartNotification from './components/CartNotification';
import './App.css';

// Landing page loads eagerly (it's the entry route) so first paint isn't gated by a chunk fetch.
import LandingPage from './pages/LandingPage';

// Every other page is code-split so the initial bundle stays small.
// Admin pages in particular are never shipped to first-time customer visitors.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const VenuesPage = lazy(() => import('./pages/VenuesPage'));
const VenueDetailPage = lazy(() => import('./pages/VenueDetailPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const MyBookingsPage = lazy(() => import('./pages/MyBookingsPage'));
const OpenPlayPage = lazy(() => import('./pages/OpenPlayPage'));
const PlayersPage = lazy(() => import('./pages/PlayersPage'));
const MembershipsPage = lazy(() => import('./pages/MembershipsPage'));
const EquipmentPage = lazy(() => import('./pages/EquipmentPage'));
const AddOnsPage = lazy(() => import('./pages/AddOnsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const TournamentListingPage = lazy(() => import('./pages/TournamentListingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const Rates = lazy(() => import('./pages/Rates'));
const Location = lazy(() => import('./pages/Location'));
const CourtRules = lazy(() => import('./pages/CourtRules'));
const PaymentCallbackPage = lazy(() => import('./pages/PaymentCallbackPage'));
const SuccessPaymentPage = lazy(() => import('./pages/SuccessPaymentPage'));
const TournamentPaymentCallbackPage = lazy(() => import('./pages/TournamentPaymentCallbackPage'));
const TournamentSuccessPaymentPage = lazy(() => import('./pages/TournamentSuccessPaymentPage'));

// Admin Pages (lazy)
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminBookingsPage = lazy(() => import('./pages/admin/AdminBookingsPage'));
const AdminCourtsPage = lazy(() => import('./pages/admin/AdminCourtsPage'));
const AdminPOSPage = lazy(() => import('./pages/admin/AdminPOSPage'));
const AdminAddOnsPage = lazy(() => import('./pages/admin/AdminAddOnsPage'));
const AdminEventsPage = lazy(() => import('./pages/admin/AdminEventsPage'));
const AdminUsersManagePage = lazy(() => import('./pages/admin/AdminUsersManagePage'));
const AdminSupportPage = lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminPaymentsPage = lazy(() => import('./pages/admin/AdminPaymentsPage'));
const AdminOpenPlayPage = lazy(() => import('./pages/admin/AdminOpenPlayPage'));
const AdminReviewsPage = lazy(() => import('./pages/admin/AdminReviewsPage'));
const AdminRewardsPage = lazy(() => import('./pages/admin/AdminRewardsPage'));
const AdminMembershipsPage = lazy(() => import('./pages/admin/AdminMembershipsPage'));
const AdminEquipmentPage = lazy(() => import('./pages/admin/AdminEquipmentPage'));
const AdminNotificationsPage = lazy(() => import('./pages/admin/AdminNotificationsPage'));
const AdminFAQsPage = lazy(() => import('./pages/admin/AdminFAQsPage'));
const AdminVenuePage = lazy(() => import('./pages/admin/AdminVenuePage'));
const AdminGalleryPage = lazy(() => import('./pages/admin/AdminGalleryPage'));
const AdminTransactionsPage = lazy(() => import('./pages/admin/AdminTransactionsPage'));
const AdminSuccessPaymentPage = lazy(() => import('./pages/admin/AdminSuccessPaymentPage'));
const AdminSpecialPackagesPage = lazy(() => import('./pages/admin/AdminSpecialPackagesPage'));
const AdminPOSBookingPage = lazy(() => import('./pages/admin/AdminPOSBookingPage'));
const AdminKioskBookingsPage = lazy(() => import('./pages/admin/AdminKioskBookingsPage'));
const AdminEmbedPage = lazy(() => import('./pages/admin/AdminEmbedPage'));
const AdminLogsPage = lazy(() => import('./pages/admin/AdminLogsPage'));
const AdminBookingHistoryPage = lazy(() => import('./pages/admin/AdminBookingHistoryPage'));
const AdminWebLog = lazy(() => import('./pages/admin/AdminWebLog'));

const GOOGLE_CLIENT_ID = '368879857036-48hri1ctil5cs1kfkucfq805vkkfmc4a.apps.googleusercontent.com';

// Lightweight fallback shown while a route chunk is being fetched.
function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#1e4b85] border-t-transparent" />
    </div>
  );
}

function CustomerRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/rates" element={<Rates />} />
      <Route path="/location" element={<Location />} />
      <Route path="/court-rules" element={<CourtRules />} />
      <Route path="/tournaments" element={<TournamentListingPage />} />
      
      {/* Protected Routes */}
      <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/venues" element={<ProtectedRoute><VenuesPage /></ProtectedRoute>} />
      <Route path="/venues/:venueId" element={<ProtectedRoute><VenueDetailPage /></ProtectedRoute>} />
      <Route path="/book/:venueId" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
      <Route path="/payment/callback" element={<ProtectedRoute><PaymentCallbackPage /></ProtectedRoute>} />
      <Route path="/tournament-payment/callback" element={<ProtectedRoute><TournamentPaymentCallbackPage /></ProtectedRoute>} />
      <Route path="/my-bookings" element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
      <Route path="/open-play" element={<ProtectedRoute><OpenPlayPage /></ProtectedRoute>} />
      <Route path="/players" element={<ProtectedRoute><PlayersPage /></ProtectedRoute>} />
      <Route path="/memberships" element={<ProtectedRoute><MembershipsPage /></ProtectedRoute>} />
      <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
      <Route path="/add-ons" element={<ProtectedRoute><AddOnsPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      {/* <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} /> */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/success-payment" element={<ProtectedRoute><SuccessPaymentPage /></ProtectedRoute>} />
      <Route path="/tournament-success-payment" element={<ProtectedRoute><TournamentSuccessPaymentPage /></ProtectedRoute>} />
    </Routes>
    </Suspense>
  );
}

function AdminRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Admin Public Route */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      
      {/* Admin Protected Routes */}
      <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      <Route path="/admin/users" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminUsersPage /></AdminProtectedRoute>} />
      <Route path="/admin/bookings" element={<AdminProtectedRoute><AdminBookingsPage /></AdminProtectedRoute>} />
      <Route path="/admin/special-packages" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminSpecialPackagesPage /></AdminProtectedRoute>} />
      <Route path="/admin/courts" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor']}><AdminCourtsPage /></AdminProtectedRoute>} />
      <Route path="/admin/pos" element={<AdminProtectedRoute><AdminPOSPage /></AdminProtectedRoute>} />
      <Route path="/admin/transactions" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminTransactionsPage /></AdminProtectedRoute>} />
      <Route path="/admin/booking-history" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminBookingHistoryPage /></AdminProtectedRoute>} />
      <Route path="/admin/addons" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminAddOnsPage /></AdminProtectedRoute>} />
      <Route path="/admin/events" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'umpire']}><AdminEventsPage /></AdminProtectedRoute>} />
      <Route path="/admin/admins" element={<AdminProtectedRoute allowedRoles={['super_admin']}><AdminUsersManagePage /></AdminProtectedRoute>} />
      <Route path="/admin/support" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor']}><AdminSupportPage /></AdminProtectedRoute>} />
      <Route path="/admin/payments" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminPaymentsPage /></AdminProtectedRoute>} />
      <Route path="/admin/open-play" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor']}><AdminOpenPlayPage /></AdminProtectedRoute>} />
      <Route path="/admin/reviews" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminReviewsPage /></AdminProtectedRoute>} />
      <Route path="/admin/rewards" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminRewardsPage /></AdminProtectedRoute>} />
      <Route path="/admin/memberships" element={<AdminProtectedRoute allowedRoles={['super_admin']}><AdminMembershipsPage /></AdminProtectedRoute>} />
      <Route path="/admin/equipment" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminEquipmentPage /></AdminProtectedRoute>} />
      <Route path="/admin/gallery" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminGalleryPage /></AdminProtectedRoute>} />
      <Route path="/admin/notifications" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminNotificationsPage /></AdminProtectedRoute>} />
      <Route path="/admin/faqs" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminFAQsPage /></AdminProtectedRoute>} />
      <Route path="/admin/venue" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminVenuePage /></AdminProtectedRoute>} />
      <Route path="/admin/embed" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminEmbedPage /></AdminProtectedRoute>} />
      <Route path="/admin/payment-successful" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminSuccessPaymentPage /></AdminProtectedRoute>} />
      <Route path="/admin/pos-bookings" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminPOSBookingPage /></AdminProtectedRoute>} />
      <Route path="/admin/kiosk-bookings" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminKioskBookingsPage /></AdminProtectedRoute>} />
      <Route path="/admin/web-log" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminWebLog /></AdminProtectedRoute>} />
      <Route path="/admin/logs" element={<AdminProtectedRoute allowedRoles={['super_admin']}><AdminLogsPage /></AdminProtectedRoute>} />
    </Routes>
    </Suspense>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // Route to admin section
  if (location.pathname.startsWith('/admin')) {
    return (
      <AdminAuthProvider>
        <AdminNotificationsProvider>
          <AdminThemeProvider>
            <AdminRouter />
          </AdminThemeProvider>
        </AdminNotificationsProvider>
      </AdminAuthProvider>
    );
  }
  
  // Route to customer section
  return (
    <AuthProvider>
      <NotificationsProvider>
        <CartProvider>
          <CustomerRouter />
          <CartModal />
          <CartNotification />
        </CartProvider>
      </NotificationsProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <ThemeProvider>
          <RealtimeProvider>
            <AppRouter />
            <Toaster position="top-right" richColors closeButton duration={4500} />
          </RealtimeProvider>
        </ThemeProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
